import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCancelableTask } from './useCancelableTask';
import { bootstrapSolowSmithGof } from '../models/bootstrap';
import { fitGompertz } from '../models/gompertz';
import { fitLogistic } from '../models/logistic';
import { fitSolowSmithCore } from '../models/solowSmith';
import { recommendThresholds } from '../utils/recommendation';
import { createBootstrapWorkerClient } from '../utils/bootstrapWorkerClient';

export const useRecommendation = ({
  groupStats,
  endYear,
  unitType,
  filteredCount,
  minGroupEvents,
  minActiveYears,
  minSpanYears,
  solowGuardEnabled,
  solowMaxRatio,
  solowMinR2,
  curveGuardEnabled,
  curveMinR2,
  recommendBootstrapSamples,
  curveFitMethod,
  randomSeed,
  getRandomFn,
  t,
}) => {
  const task = useCancelableTask();
  const workerRef = useRef(null);
  const groupCount = groupStats.length;

  const disabledReason = useMemo(() => {
    if (unitType === 'subfamily') return t('estimationPage.recommendDisabledSubfamily');
    if (filteredCount === 0) return t('noData');
    if (groupCount < 2) return t('estimationPage.recommendDisabledSingleGroup');
    return '';
  }, [unitType, filteredCount, groupCount, t]);

  const canRecommend = !disabledReason;

  useEffect(() => {
    task.cancel();
    task.reset();
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [
    task.cancel,
    task.reset,
    groupStats,
    filteredCount,
    endYear,
    minGroupEvents,
    minActiveYears,
    minSpanYears,
    solowGuardEnabled,
    solowMaxRatio,
    solowMinR2,
    curveGuardEnabled,
    curveMinR2,
    recommendBootstrapSamples,
    curveFitMethod,
    randomSeed,
  ]);

  const runRecommendation = useCallback((method) => {
    if (!canRecommend || task.loading) return;
    const existing = task.result || {};

    task.run(async ({ updateStage, updateProgress, setResult, setError, yieldToMain, isActive }) => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      const workerClient = createBootstrapWorkerClient();
      workerRef.current = workerClient;

      updateStage(
        method === 'bootstrap'
          ? t('estimationPage.recommendBootstrapRunning')
          : t('estimationPage.recommendFastRunning'),
        5
      );

      try {
        const randomFn = getRandomFn();
        const yieldEvery = 10;

        if (!groupStats.length) {
          setError(t('noData'));
          return;
        }

        const stableMap = new Map();
        for (let i = 0; i < groupStats.length; i += 1) {
          if (!isActive()) return;
          const stat = groupStats[i];
          const progress = 10 + ((i + 1) / groupStats.length) * 60;
          updateStage(
            method === 'bootstrap'
              ? t('estimationPage.recommendBootstrapEvaluating', { current: i + 1, total: groupStats.length })
              : t('estimationPage.recommendFastEvaluating', { current: i + 1, total: groupStats.length }),
            progress
          );
          await yieldToMain();

          const series = stat.series;
          const modelStart = stat.modelStartYear;
          const modelDiscovery = stat.modelDiscoveryCounts;
          const modelFitPointsLocal = stat.modelFitPoints;
          if (modelFitPointsLocal.length < 5 || series.currentCount <= 0) {
            stableMap.set(stat.key, false);
            continue;
          }
          const solowObserved = modelFitPointsLocal.map(point => ({ x: point.year, y: point.y }));
          const solow = fitSolowSmithCore(modelDiscovery, modelStart, endYear, solowObserved);
          const logistic = fitLogistic(modelFitPointsLocal, {
            discoveryCounts: modelDiscovery,
            modelStartYear: modelStart,
            fitMethod: curveFitMethod,
          });
          const gompertz = fitGompertz(modelFitPointsLocal, {
            discoveryCounts: modelDiscovery,
            modelStartYear: modelStart,
            fitMethod: curveFitMethod,
          });

          const curveThreshold = curveGuardEnabled && Number.isFinite(curveMinR2) && curveMinR2 > 0
            ? curveMinR2
            : 0;
          const logisticStable = Boolean(
            logistic && Number.isFinite(logistic.rSquared) && logistic.rSquared >= curveThreshold
          );
          const gompertzStable = Boolean(
            gompertz && Number.isFinite(gompertz.rSquared) && gompertz.rSquared >= curveThreshold
          );

          let solowStable = false;
          if (solow && Number.isFinite(solow.total)) {
            const ratio = solow.total / series.currentCount;
            const ratioOk = !solowGuardEnabled
              || !Number.isFinite(solowMaxRatio)
              || solowMaxRatio <= 0
              || (Number.isFinite(ratio) && ratio <= solowMaxRatio);
            const r2Ok = !solowGuardEnabled
              || !Number.isFinite(solowMinR2)
              || solowMinR2 <= 0
              || (Number.isFinite(solow.rSquared) && solow.rSquared >= solowMinR2);

            if (method === 'bootstrap') {
              const gof = workerClient
                ? await workerClient.runJob('BOOTSTRAP_SOLOW_GOF', {
                    fit: solow,
                    discoveryCounts: modelDiscovery,
                    startYear: modelStart,
                    endYear,
                    iterations: recommendBootstrapSamples,
                    randomSeed,
                    sharedRandom: Boolean(randomSeed),
                  })
                : await bootstrapSolowSmithGof(
                    solow,
                    modelDiscovery,
                    modelStart,
                    endYear,
                    recommendBootstrapSamples,
                    {
                      randomFn,
                      yieldEvery,
                      yieldFn: yieldToMain,
                    }
                  );
              solowStable = ratioOk && r2Ok && Boolean(gof && Number.isFinite(gof.pValue) && gof.pValue >= 0.1);
            } else {
              solowStable = ratioOk && r2Ok;
            }
          }

          const stable = solowStable || logisticStable || gompertzStable;
          stableMap.set(stat.key, stable);
        }

        updateStage(t('estimationPage.recommendSearching'), 80);
        await yieldToMain();

        const recommendation = recommendThresholds(groupStats, stableMap, {
          current: {
            minEvents: minGroupEvents,
            minNonZeroYears: minActiveYears,
            minSpanYears: minSpanYears,
          },
        });

        if (!recommendation) {
          setError(t('estimationPage.recommendNoResult'));
          return;
        }

        const next = {
          ...existing,
          [method]: { ...recommendation, method },
        };

        setResult(next);
        updateStage(t('estimationPage.recommendDone'), 95);
        updateProgress(100);
      } catch (error) {
        if (isActive()) setError(t('estimationPage.recommendNoResult'));
      } finally {
        if (workerRef.current) {
          workerRef.current.terminate();
          workerRef.current = null;
        }
      }
    }, { preserveResult: true });
  }, [
    canRecommend,
    task.loading,
    task.result,
    task.run,
    groupStats,
    endYear,
    minGroupEvents,
    minActiveYears,
    minSpanYears,
    solowGuardEnabled,
    solowMaxRatio,
    solowMinR2,
    curveGuardEnabled,
    curveMinR2,
    recommendBootstrapSamples,
    curveFitMethod,
    randomSeed,
    getRandomFn,
    t,
  ]);

  const results = task.result || {};

  return {
    ...task,
    fastResult: results.fast || null,
    bootstrapResult: results.bootstrap || null,
    disabledReason,
    canRecommend,
    runRecommendation,
  };
};
